import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import type { QuestionType } from '../../types';

interface Props {
  id: string;
  type: QuestionType;
  value: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isValid: boolean;
}

export function TextQuestion({
  id,
  type,
  value,
  placeholder,
  minLength,
  maxLength,
  onChange,
  onSubmit,
  isValid,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (type === 'text-long') {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [type]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && isValid && type !== 'text-long') {
      e.preventDefault();
      // stopPropagation evita que el handler padre en ApplicationPage
      // dispare handleNext una segunda vez por bubbling y haga doble goNext.
      e.stopPropagation();
      onSubmit();
    }
  };

  if (type === 'text-long') {
    const len = value.length;
    const belowMin = minLength !== undefined && len < minLength;
    const counterColor = belowMin ? 'text-hit-error' : 'text-hit-gray-mid';

    return (
      <div className="flex flex-col gap-2">
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={5}
          aria-required="true"
          aria-describedby={`${id}-counter`}
          className="hit-input resize-none leading-relaxed"
        />
        <p id={`${id}-counter`} className={`text-xs text-right font-medium ${counterColor}`}>
          {len}/{maxLength} caracteres{minLength !== undefined ? ` (mínimo ${minLength})` : ''}
        </p>
      </div>
    );
  }

  const inputType = type === 'email' ? 'email' : 'text';

  return (
    <input
      ref={inputRef}
      id={id}
      type={inputType}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      autoComplete={type === 'email' ? 'email' : 'off'}
      inputMode={type === 'email' ? 'email' : 'text'}
      aria-required="true"
      className="hit-input"
    />
  );
}
